import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { EntityUpdate } from "components/ManageTags/types";
import { WorkflowTagPickerAdapter } from "components/ManageTags/WorkflowTagPickerAdapter";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";
import { UPDATE_WORKFLOW_TAGS_BULK_MUTATION } from "routes/Staff/Fulfillment/WorkflowActivitiesTable/ManageWorkflowTags";

import { UnifiedWorkRow } from "./types";

const LOCAL_STORAGE_KEY = "workflow-recent-tags";

/**
 * Tag editor for one or many queue rows — the same ManageTagsFormDialog +
 * bulk mutation the fulfillment grid uses, so labels behave identically on
 * both surfaces (shared recent-tags list included).
 */
export function WorkTagsDialog({
  rows,
  open,
  onClose,
  onSaved,
}: {
  rows: UnifiedWorkRow[];
  open: boolean;
  onClose: () => void;
  /** Called after a successful save (refetch the queue here). */
  onSaved: () => void;
}) {
  const alerts = useAlerts();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [updateWorkflowTags, { loading }] = useMutation(UPDATE_WORKFLOW_TAGS_BULK_MUTATION);

  const taggedRows = useMemo(
    () => rows.map((row) => ({ id: row.workflowId, tags: row.tags })),
    [rows]
  );
  const getEntityId = useCallback((row: { id: string }) => row.id, []);

  const handleSubmit = useCallback(
    async (updates: EntityUpdate[]) => {
      try {
        const workflowUpdates = updates.map((update) => ({
          workflowId: update.id,
          tagNames: update.tags,
        }));
        if (workflowUpdates.length > 0) {
          await updateWorkflowTags({ variables: { workflowUpdates } });
        }
        alerts.success(
          `Updated tags for ${workflowUpdates.length} ${pluralize("work item", workflowUpdates.length)}`
        );
        onSaved();
        setShowConfirmation(false);
        onClose();
      } catch (err) {
        alerts.error("An error occurred while updating tags", err);
      }
    },
    [updateWorkflowTags, alerts, onSaved, onClose]
  );

  const handleClose = useCallback(() => {
    setShowConfirmation(false);
    onClose();
  }, [onClose]);

  return (
    <ManageTagsFormDialog
      selectedRows={taggedRows}
      getEntityId={getEntityId}
      onClose={handleClose}
      onCloseConfirmation={() => setShowConfirmation(false)}
      confirm={() => setShowConfirmation(true)}
      open={open}
      withConfirmation
      showConfirmation={showConfirmation}
      onSubmit={handleSubmit}
      loading={loading}
      entityName="Work item"
      localStorageKey={LOCAL_STORAGE_KEY}
      TagPickerComponent={WorkflowTagPickerAdapter}
    />
  );
}
